import { useMemo } from "react";
import { useSelector } from "react-redux";

const isCellVisible = cell => {
  if (!cell) return false;
  if (cell.isCustomValue) return true;
  const value = cell.value;
  if (value == null || value === "") return false;
  if (typeof value === "boolean" && !value) return false;
  return true;
};

/**
 * Resolves which summary bar cells to show based on user preferences.
 *
 * @param {string} module Preference key (e.g. sale_order_summary).
 * @param {Array<{ id: string, label: string }>} fieldCatalogue All configurable fields.
 * @param {Array<{ id: string, label?: string, value?: *, isCustomValue?: boolean }>} cells
 */
export const useSummaryBarFields = (module, fieldCatalogue, cells) => {
  const savedIds = useSelector(state => state.auth.summaryBarFields?.[module]);

  const selectedIds = useMemo(() => {
    const catalogueIds = fieldCatalogue.map(field => field.id);
    if (!savedIds?.length) {
      return catalogueIds;
    }
    const knownIds = new Set(catalogueIds);
    return savedIds.filter(id => knownIds.has(id));
  }, [fieldCatalogue, savedIds]);

  const cellsById = useMemo(() => {
    const map = new Map();
    cells.forEach(cell => {
      if (cell?.id) {
        map.set(cell.id, cell);
      }
    });
    return map;
  }, [cells]);

  const visibleCells = useMemo(
    () =>
      selectedIds
        .map(id => cellsById.get(id))
        .filter(isCellVisible)
        .map(cell => ({
          label: cell.label,
          value: cell.value,
          isCustomValue: cell.isCustomValue,
        })),
    [selectedIds, cellsById],
  );

  return { visibleCells, selectedIds };
};
